// Phone-alarm-style ringtones for TimeFlash. All synthesized via Web Audio API —
// no audio files, no network, works offline. Each tone exposes { start, stop }.

let ctx = null;
const ensureCtx = () => {
  if (!ctx || ctx.state === "closed") {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
};

export const primeAudio = () => {
  ensureCtx();
};

// --- Tone registry: each key maps to a start fn returning a stop fn ---

const makeTone = (startFn) => {
  let stopper = null;
  return {
    start: () => {
      if (stopper) return;
      stopper = startFn();
    },
    stop: () => {
      try {
        stopper?.();
      } catch (_) {}
      stopper = null;
    },
  };
};

// --- Primitive helpers ---

const chirp = (freq, duration = 0.18, type = "square", vol = 0.22) => {
  const c = ensureCtx();
  if (!c) return;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  g.gain.value = 0;
  osc.connect(g).connect(c.destination);
  const now = c.currentTime;
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(vol, now + 0.01);
  g.gain.linearRampToValueAtTime(0, now + duration);
  osc.start(now);
  osc.stop(now + duration + 0.02);
};

// --- Tone implementations ---

const beepTone = makeTone(() => {
  chirp(880, 0.18);
  const id = window.setInterval(() => chirp(880, 0.18), 500);
  return () => window.clearInterval(id);
});

const radarTone = makeTone(() => {
  // iPhone-ish "Radar": rising three-note chirps every 1.2s
  const seq = () => {
    chirp(660, 0.12, "sine", 0.26);
    window.setTimeout(() => chirp(880, 0.12, "sine", 0.26), 150);
    window.setTimeout(() => chirp(1100, 0.14, "sine", 0.28), 300);
  };
  seq();
  const id = window.setInterval(seq, 1200);
  return () => window.clearInterval(id);
});

const sirenTone = makeTone(() => {
  const c = ensureCtx();
  if (!c) return () => {};
  const gain = c.createGain();
  gain.gain.value = 0;
  gain.connect(c.destination);
  const osc1 = c.createOscillator();
  osc1.type = "sawtooth";
  osc1.frequency.value = 440;
  osc1.connect(gain);
  const osc2 = c.createOscillator();
  osc2.type = "square";
  osc2.frequency.value = 445;
  osc2.connect(gain);
  const lfo = c.createOscillator();
  lfo.type = "sine";
  lfo.frequency.value = 2.5;
  const lfoGain = c.createGain();
  lfoGain.gain.value = 220;
  lfo.connect(lfoGain);
  lfoGain.connect(osc1.frequency);
  lfoGain.connect(osc2.frequency);
  const now = c.currentTime;
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.3, now + 0.08);
  osc1.start(now);
  osc2.start(now);
  lfo.start(now);
  return () => {
    const n = c.currentTime;
    try {
      gain.gain.cancelScheduledValues(n);
      gain.gain.setValueAtTime(gain.gain.value, n);
      gain.gain.linearRampToValueAtTime(0, n + 0.06);
    } catch (_) {}
    const t = n + 0.08;
    try { osc1.stop(t); } catch (_) {}
    try { osc2.stop(t); } catch (_) {}
    try { lfo.stop(t); } catch (_) {}
  };
});

const marimbaTone = makeTone(() => {
  // Soft two-note chime, repeating every 1.5s
  const seq = () => {
    chirp(523, 0.4, "triangle", 0.3);
    window.setTimeout(() => chirp(784, 0.5, "triangle", 0.3), 220);
  };
  seq();
  const id = window.setInterval(seq, 1500);
  return () => window.clearInterval(id);
});

const alarmClockTone = makeTone(() => {
  // Old-school alarm clock: tight, intense beep-beep-beep
  const burst = () => {
    chirp(2200, 0.08, "square", 0.3);
    window.setTimeout(() => chirp(2200, 0.08, "square", 0.3), 110);
    window.setTimeout(() => chirp(2200, 0.08, "square", 0.3), 220);
  };
  burst();
  const id = window.setInterval(burst, 650);
  return () => window.clearInterval(id);
});

export const TONES = {
  off: null,
  beep: beepTone,
  radar: radarTone,
  siren: sirenTone,
  marimba: marimbaTone,
  alarm: alarmClockTone,
};

export const TONE_LABELS = [
  { v: "off", l: "Silent" },
  { v: "beep", l: "Beep" },
  { v: "radar", l: "Radar" },
  { v: "marimba", l: "Marimba" },
  { v: "siren", l: "Siren" },
  { v: "alarm", l: "Alarm clock" },
];

export const startTone = (name) => {
  stopAllTones();
  const t = TONES[name];
  if (t) t.start();
};

export const stopAllTones = () => {
  for (const key of Object.keys(TONES)) {
    const t = TONES[key];
    try {
      t?.stop();
    } catch (_) {}
  }
};

// Backwards-compat aliases used by old imports
export const startBeeping = () => startTone("beep");
export const stopBeeping = () => stopAllTones();
export const startWarningSiren = () => startTone("siren");
export const stopWarningSiren = () => stopAllTones();
